/**
 * Client pour l'API Opus Lab (Cloudflare Worker).
 * Utilise VITE_OPUS_API_URL ; si vide, les appels ne sont pas effectués.
 */

const getBaseUrl = () => {
  const raw = (typeof import.meta.env !== 'undefined' && import.meta.env.VITE_OPUS_API_URL) || ''
  const trimmed = String(raw).trim()
  if (!trimmed) return ''
  // URL absolue requise : sans schéma, le navigateur résout par rapport à l’origine (ex. Pages) → 405
  if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed.replace(/^\/+/, '')}`
  return trimmed
}

export function isOpusApiConfigured() {
  return !!getBaseUrl().trim()
}

// --- Cache client smart-fill (mémoire + localStorage, max 50 entrées, TTL 30 j) ---
const SMARTFILL_STORAGE_KEY = 'opus_smartfill_cache'
const SMARTFILL_MAX_ENTRIES = 50
const SMARTFILL_TTL_MS = 30 * 24 * 60 * 60 * 1000

const memoryCache = new Map()

function loadFromStorage() {
  try {
    if (typeof localStorage === 'undefined') return {}
    const raw = localStorage.getItem(SMARTFILL_STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

function saveToStorage(entries) {
  try {
    if (typeof localStorage === 'undefined') return
    localStorage.setItem(SMARTFILL_STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // ignore
  }
}

/**
 * Récupère le cache smart-fill pour une vidéo (mémoire puis localStorage).
 * @param {string} videoId - ID YouTube (11 caractères)
 * @returns {{ parsed: object, movements?: string[], imslpLinkFound?: boolean, imslpUrlUsed?: string | null } | null}
 */
export function getSmartFillCache(videoId) {
  if (!videoId || String(videoId).length !== 11) return null
  const id = String(videoId)
  const fromMem = memoryCache.get(id)
  if (fromMem) {
    if (fromMem.ts && Date.now() - fromMem.ts > SMARTFILL_TTL_MS) {
      memoryCache.delete(id)
      return null
    }
    return {
      parsed: fromMem.parsed || {},
      movements: fromMem.movements,
      imslpLinkFound: fromMem.imslpLinkFound,
      imslpUrlUsed: fromMem.imslpUrlUsed ?? null,
    }
  }
  const stored = loadFromStorage()
  const entry = stored[id]
  if (!entry) return null
  if (entry.ts && Date.now() - entry.ts > SMARTFILL_TTL_MS) {
    delete stored[id]
    saveToStorage(stored)
    return null
  }
  memoryCache.set(id, entry)
  return {
    parsed: entry.parsed || {},
    movements: entry.movements,
    imslpLinkFound: entry.imslpLinkFound,
    imslpUrlUsed: entry.imslpUrlUsed ?? null,
  }
}

/**
 * Enregistre le cache smart-fill pour une vidéo.
 * @param {string} videoId - ID YouTube
 * @param {{ parsed: object, movements?: string[], imslpLinkFound?: boolean, imslpUrlUsed?: string | null }} data
 */
export function setSmartFillCache(videoId, data) {
  if (!videoId || !data?.parsed) return
  const id = String(videoId)
  const entry = {
    parsed: data.parsed,
    movements: data.movements || [],
    imslpLinkFound: data.imslpLinkFound === true,
    imslpUrlUsed: data.imslpUrlUsed ?? null,
    ts: Date.now(),
  }
  memoryCache.set(id, entry)
  const stored = loadFromStorage()
  stored[id] = entry
  const keys = Object.keys(stored)
  if (keys.length > SMARTFILL_MAX_ENTRIES) {
    const sorted = keys.sort((a, b) => (stored[a].ts || 0) - (stored[b].ts || 0))
    for (let i = 0; i < sorted.length - SMARTFILL_MAX_ENTRIES; i++) {
      delete stored[sorted[i]]
      memoryCache.delete(sorted[i])
    }
  }
  saveToStorage(stored)
}

/**
 * Recherche YouTube via le Worker (niveaux 1 → 2 → 3).
 * @param {string} q - Requête de recherche
 * @returns {Promise<{ items: Array<{ id: string, title: string, channelTitle: string, thumbnail: string }>, source?: string }>}
 */
export async function searchYoutube(q) {
  const base = getBaseUrl().trim()
  if (!base) return { items: [], source: 'none' }
  const url = `${base.replace(/\/$/, '')}/api/youtube-search?q=${encodeURIComponent(String(q).trim())}`
  const res = await fetch(url)
  if (!res.ok) {
    let message = res.status === 400 ? 'Requête invalide' : 'Recherche indisponible'
    try {
      const body = await res.json()
      if (body?.error && typeof body.error === 'string') message = body.error
    } catch {
      // garder le message par défaut si la réponse n'est pas du JSON
    }
    throw new Error(message)
  }
  return res.json()
}

/**
 * Parse un titre vidéo avec Gemini (compositeur, œuvre, mouvement).
 * Envoie videoId pour permettre le cache serveur (KV).
 * @param {string} title - Titre brut
 * @param {string} [videoId] - ID YouTube (11 caractères) pour cache serveur
 * @param {{ skipCache?: boolean }} [options] - skipCache: true pour forcer un nouvel appel Gemini (ignorer le cache)
 * @returns {Promise<{ composer: string, workTitle: string, movementTitle: string } | null>} null si API non configurée ou erreur
 */
export async function parseTitle(title, videoId, options = {}) {
  const base = getBaseUrl().trim()
  if (!base || !title?.trim()) return null
  const url = `${base.replace(/\/$/, '')}/api/parse-title`
  const body = { title: String(title).trim().slice(0, 500) }
  if (videoId && String(videoId).length === 11) body.videoId = String(videoId)
  if (options.skipCache === true) body.skipCache = true
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) return null
    const data = await res.json()
    if (data && typeof data.composer !== 'undefined') {
      return {
        composer: data.composer ?? '',
        workTitle: data.workTitle ?? '',
        movementTitle: data.movementTitle ?? '',
        catalog: data.catalog ?? '',
        key: data.key ?? '',
      }
    }
    return null
  } catch {
    return null
  }
}

/**
 * Récupère la liste des mouvements pour une œuvre (Worker : cascade IMSLP puis Wikipedia).
 * @param {string} composer - Nom du compositeur
 * @param {string} work - Titre de l'œuvre
 * @param {{ catalog?: string }} [options] - catalog (ex. BWV.826) pour la recherche IMSLP
 * @returns {Promise<{ movements: string[], imslpLinkFound?: boolean, imslpUrlUsed?: string | null }>}
 */
export async function getMovements(composer, work, options = {}) {
  const empty = { movements: [], imslpLinkFound: false, imslpUrlUsed: null }
  const base = getBaseUrl().trim()
  if (!base || !composer?.trim() || !work?.trim()) return empty
  let url = `${base.replace(/\/$/, '')}/api/movements?composer=${encodeURIComponent(composer.trim())}&work=${encodeURIComponent(work.trim())}`
  const catalog = options?.catalog?.trim()
  if (catalog) url += `&catalog=${encodeURIComponent(catalog)}`
  try {
    const res = await fetch(url)
    if (!res.ok) return empty
    const data = await res.json()
    return {
      movements: Array.isArray(data.movements) ? data.movements : [],
      imslpLinkFound: data.imslpLinkFound === true,
      imslpUrlUsed: data.imslpUrlUsed ?? null,
    }
  } catch {
    return empty
  }
}

/**
 * Précharge le smart-fill pour une vidéo (parse-title + movements) et remplit le cache.
 * À appeler dès que videoId et titre sont dispo (ex. entrée dans l’éditeur). Si le cache est déjà rempli, ne fait rien.
 * @param {{ videoId: string, videoTitle?: string, getVideoTitle?: () => string | null }} opts
 */
export async function preloadSmartFillForVideo(opts) {
  const { videoId, videoTitle, getVideoTitle } = opts || {}
  if (!videoId || String(videoId).length !== 11) return
  if (getSmartFillCache(videoId)) return

  let title = (videoTitle && String(videoTitle).trim() && String(videoTitle).trim() !== 'Vidéo YouTube') ? String(videoTitle).trim() : null
  if (!title && typeof getVideoTitle === 'function') {
    try {
      title = getVideoTitle() || null
    } catch {
      title = null
    }
  }
  if (!title) return

  const parsed = await parseTitle(title, videoId)
  if (!parsed || (!parsed.composer && !parsed.workTitle)) return

  const composer = (parsed.composer || '').trim()
  const work = (parsed.workTitle || '').trim()
  const catalog = (parsed.catalog || '').trim()
  let movements = []
  let imslpLinkFound = false
  let imslpUrlUsed = null
  if (composer && work) {
    const result = await getMovements(composer, work, catalog ? { catalog } : {})
    movements = result.movements ?? []
    imslpLinkFound = result.imslpLinkFound === true
    imslpUrlUsed = result.imslpUrlUsed ?? null
  }
  setSmartFillCache(videoId, {
    parsed: {
      composer: parsed.composer ?? '',
      workTitle: parsed.workTitle ?? '',
      movementTitle: parsed.movementTitle ?? '',
      catalog: parsed.catalog ?? '',
      key: parsed.key ?? '',
    },
    movements,
    imslpLinkFound,
    imslpUrlUsed,
  })
}

/**
 * Récupère l'usage quotidien des API (YouTube, Gemini) et les limites.
 * @returns {Promise<{ available: boolean, date?: string, youtube?: number, gemini?: number, limits?: { youtube: number, gemini: number }, estimatedExercisesLeft?: number, message?: string } | null>} null si API non configurée ou erreur
 */
export async function getUsage() {
  const base = getBaseUrl().trim()
  if (!base) return null
  const url = `${base.replace(/\/$/, '')}/api/usage`
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    return res.json()
  } catch {
    return null
  }
}
